import React, { useState, FC, useEffect } from "react";
import {makeStyles, Theme} from "@material-ui/core/styles";
import {Grid, Button, Dialog, DialogActions, DialogTitle, Typography, FormLabel, FormControl, FormGroup, FormControlLabel, Checkbox} from "@material-ui/core";
import {initialSpecialtiesSelected, initialDaysSelected, initialDayTimesSelected, intervals} from "../../Constants/filterVetsParams";
import {colorInputBorder} from "../../Constants/theme";
import moment from "moment";

const useStyles = makeStyles((theme: Theme) =>({
    dialog: {
        padding: "48px",
        "& .MuiFormControlLabel-label": {
            minWidth: "100px"
        },
        [theme.breakpoints.down("xs")]: {
            padding: " 28px 10px"
        }
    },
    dialogTitle: {
        fontSize: "24px",
        fontWeight: "bold",
        padding: "0"
    },
    section: {
        marginTop: "34px",
        marginBottom: "20px",
        fontSize: "18px",
        fontWeight: 700
    },
    actions: {
        marginTop: "48px"
    },
    button: {
        padding: "12px 32px",
        color: colorInputBorder,
        border: `1px solid ${colorInputBorder}`
    },
}));

interface FilterParams {
	specialties?: Array<string>,
	workingDayOfWeek?: Array<string>,
	startDate?: any,
	endDate?: any,
}
const mergeIntervals = (intervals, order = "direct") => {
    let merged = [];
    switch (order){
        case "direct":
            merged.push(intervals[0].from);
            merged.push(intervals[1].to);
            break;
        case "reverse":
            merged.push(intervals[1].from);
            merged.push(intervals[0].to);
            break;
    }
    return merged;
};
// eslint-disable-next-line react/prop-types
const Filter = ({isOpen, setOpenDialog, handleChange})  => {
    const classes = useStyles();
    const [specialties, setSpecialties] = useState(initialSpecialtiesSelected);
    const [days, setDays] = useState(initialDaysSelected);
    const [dateTime, setDateTime] = useState(initialDayTimesSelected);
    const { DOGS, CATS, HORSES, COWS, GOATS, SHEEP, PIGS, CHICKEN, LLAMAS, ALPACAS, BIRDS, FISH, REPTILES, BUNNIES, HAMSTERS, GUINEA_PIGS, RATS, FERRETS } = specialties;
    const { MONDAY, TUESDAY,WEDNESDAY, THURSDAY, FRIDAY, SATURDAY, SUNDAY } = days;
    const { MORNINGS, MIDDAY, EVENINGS } = dateTime;
    const [changes, setChanges] = useState({});
    const [filtersApplied, setFiltersApplied] = useState(false);

    const handleClose = () => {
        Object.keys(specialties).forEach((key) => {
            // eslint-disable-next-line no-prototype-builtins
            if(changes.hasOwnProperty(key)) specialties[key] = !changes[key];
        });
        Object.keys(days).forEach((key) => {
            // eslint-disable-next-line no-prototype-builtins
            if(changes.hasOwnProperty(key)) days[key] = !changes[key];
        });
        Object.keys(dateTime).forEach((key) => {
            // eslint-disable-next-line no-prototype-builtins
            if(changes.hasOwnProperty(key)) dateTime[key] = !changes[key];
        });
        setOpenDialog(false);
    };

    const handleSpecialtiesChange = (event) => {
        setChanges({ ...changes, [event.target.name]: event.target.checked });
        setSpecialties({ ...specialties, [event.target.name]: event.target.checked });
    };

    const handleDaysChange = (event) => {
        setChanges({ ...changes, [event.target.name]: event.target.checked });
        setDays({ ...days, [event.target.name]: event.target.checked });
    };

    const handleTimeChange = (event) => {
        setChanges({ ...changes, [event.target.name]: event.target.checked });
        setDateTime({ ...dateTime, [event.target.name]: event.target.checked });
    };

    const resetFilters = () => {
        setSpecialties(initialSpecialtiesSelected);
        setDays(initialDaysSelected);
        setDateTime(initialDayTimesSelected);
        setFiltersApplied(false);
    };

    const filter = () => {
        setFiltersApplied(true);
        setChanges({});
        let params:FilterParams = {};

        Object.keys(specialties).forEach((key) => {
            if(specialties[key]) {
                !params.specialties ? params.specialties = [key] : params.specialties.push(key);
            }
        });

        Object.keys(days).forEach((key) => {
            if(days[key]) {
                !params.workingDayOfWeek ? params.workingDayOfWeek = [key] : params.workingDayOfWeek.push(key);
            }
        });
        let checkedTimes = Object.keys(dateTime).filter(key => dateTime[key]);
        switch (checkedTimes.length) {
            case 1:
                params.startDate = moment.tz(intervals[checkedTimes[0]].from, moment.tz.guess()).format("HH:mmZ");
                params.endDate = moment.tz(intervals[checkedTimes[0]].to, moment.tz.guess()).format("HH:mmZ");
                break;
            case 2:
                let timesToRequest;
                let times = checkedTimes.map(key => {
                    return intervals[key];
                });
                if(checkedTimes[0] === "MORNINGS" && checkedTimes[1] === "EVENINGS"){
                    timesToRequest = mergeIntervals(times, "reverse");
                } else {
                    timesToRequest = mergeIntervals(times);
                }
                params.startDate = moment.tz(timesToRequest[0], moment.tz.guess()).format("HH:mmZ");
                params.endDate = moment.tz(timesToRequest[1], moment.tz.guess()).format("HH:mmZ");
                break;
            case 3:
                break;
        }
        handleChange(params);
        setOpenDialog(false);
    };

    useEffect(() => {
        setOpenDialog(isOpen);
    }, [isOpen]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            classes={{paper: classes.dialog}}
            scroll="body"
            disableEnforceFocus
        >
            <DialogTitle color="primary" classes={{root: classes.dialogTitle}}>
                <Typography variant="h3">Filter Results</Typography>
            </DialogTitle>
            <Typography variant="h6" classes={{root: classes.section}}>Include Specialties</Typography>
            <Grid container spacing={1} justify="space-between">
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={CATS} onChange={handleSpecialtiesChange} name="CATS" />} label="Cats"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={DOGS} onChange={handleSpecialtiesChange} name="DOGS" />} label="Dogs"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={HORSES} onChange={handleSpecialtiesChange} name="HORSES" />} label="Horses"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={COWS} onChange={handleSpecialtiesChange} name="COWS" />} label="Cows"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={GOATS} onChange={handleSpecialtiesChange} name="GOATS" />} label="Goats"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={SHEEP} onChange={handleSpecialtiesChange} name="SHEEP" />} label="Sheep"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={PIGS} onChange={handleSpecialtiesChange} name="PIGS" />} label="Pigs"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={CHICKEN} onChange={handleSpecialtiesChange} name="CHICKEN" />} label="Chicken"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={LLAMAS} onChange={handleSpecialtiesChange} name="LLAMAS" />} label="Llamas"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={ALPACAS} onChange={handleSpecialtiesChange} name="ALPACAS" />} label="Alpacas"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={BIRDS} onChange={handleSpecialtiesChange} name="BIRDS" />} label="Birds"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={FISH} onChange={handleSpecialtiesChange} name="FISH" />} label="Fish"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={REPTILES} onChange={handleSpecialtiesChange} name="REPTILES" />} label="Reptiles"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={BUNNIES} onChange={handleSpecialtiesChange} name="BUNNIES" />} label="Bunnies"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={HAMSTERS} onChange={handleSpecialtiesChange} name="HAMSTERS" />} label="Hamsters"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={GUINEA_PIGS} onChange={handleSpecialtiesChange} name="GUINEA_PIGS" />} label="Guinea pigs"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={RATS} onChange={handleSpecialtiesChange} name="RATS" />} label="Rats"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={FERRETS} onChange={handleSpecialtiesChange} name="FERRETS" />} label="Ferrets"/>
                </Grid>
            </Grid>
            <Typography variant="h6" classes={{root: classes.section}}>Available for Care</Typography>
            <Grid container justify="space-between" spacing={1}>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={MORNINGS} onChange={handleTimeChange} name="MORNINGS" />} label="Mornings" />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={MIDDAY} onChange={handleTimeChange} name="MIDDAY" />} label="Mid day" />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={EVENINGS} onChange={handleTimeChange} name="EVENINGS" />} label="Evenings" />
                </Grid>
            </Grid>
            <Typography variant="h6" classes={{root: classes.section}}>Days Available</Typography>
            <Grid container spacing={1} justify="space-between">
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={MONDAY} onChange={handleDaysChange} name="MONDAY" />} label="Monday" />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={TUESDAY} onChange={handleDaysChange} name="TUESDAY" />} label="Tuesday"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={WEDNESDAY} onChange={handleDaysChange} name="WEDNESDAY" />} label="Wednesday"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={THURSDAY} onChange={handleDaysChange} name="THURSDAY" />} label="Thursday"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={FRIDAY} onChange={handleDaysChange} name="FRIDAY" />} label="Friday"/>
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={SATURDAY} onChange={handleDaysChange} name="SATURDAY" />} label="Saturday" />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <FormControlLabel control={<Checkbox checked={SUNDAY} onChange={handleDaysChange} name="SUNDAY" />} label="Sunday" />
                </Grid>
            </Grid>
            <DialogActions classes={{root: classes.actions}}>
                {	filtersApplied &&
					<Button type="button" onClick={resetFilters} variant="contained">
					Reset Filters
					</Button>
                }
                <Button type="button" onClick={handleClose} variant="contained">
                    Cancel
                </Button>
                <Button type="button" color="secondary" variant="contained" onClick={filter}>
                    Update Results
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Filter;
